import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Link,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Avatar,
  Button,
  Alert,
  Badge,
  IconButton,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { companiesAPI } from '../services/api';
import CommentsLog from '../components/CommentsLog/CommentsLog';
import JurisdictionBadge from '../components/JurisdictionBadge/JurisdictionBadge';

const WindUpTracker: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<Array<{ jurisdiction: string; companies: any[] }>>([]);
  const [search, setSearch] = useState('');

  const fetchGroups = async () => {
    try {
      const res = await companiesAPI.getWindUp();
      setGroups(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const totalCount = useMemo(() => groups.reduce((sum, g) => sum + g.companies.length, 0), [groups]);
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        jurisdiction: g.jurisdiction,
        companies: g.companies.filter((c) =>
          [c.name, c.registrationNumber, c.status, c.comments]
            .map((v: any) => String(v || '').toLowerCase())
            .some((s: string) => s.includes(q)),
        ),
      }))
      .filter((g) => g.companies.length > 0);
  }, [groups, search]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await companiesAPI.update(id, { status });
      toast.success(`Status updated to ${status}`);
      await fetchGroups();
    } catch (e: any) {
      toast.error('Failed to update status');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const activeCompaniesCount = useMemo(
    () => groups.reduce((sum, g) => sum + g.companies.filter((c) => {
      const status = String(c.status || '').toUpperCase();
      return !(status === 'WOUND_UP' || status === 'DISSOLVED');
    }).length, 0),
    [groups],
  );

  const woundUpCount = totalCount - activeCompaniesCount;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Premium Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <AssignmentLateIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Wind Up Tracker
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Track and manage companies flagged for wind up or dissolution
            </Typography>
          </Box>
          <Tooltip title="Back to Dashboard">
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Dashboard
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'error.main' }}>
                  <CancelIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Flagged
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {totalCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'warning.main' }}>
                  <WarningAmberIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Pending Wind Up
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {activeCompaniesCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'success.main' }}>
                  <CheckCircleIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Wound Up
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {woundUpCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <BusinessIcon color="action" />
              <Typography variant="h6">Companies Flagged for Wind Up</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Refresh data">
                <IconButton aria-label="Refresh data" onClick={fetchGroups} size="small" color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Badge badgeContent={totalCount} color="error" max={999}>
                <Chip
                  icon={<SearchIcon />}
                  label="Search"
                  variant="outlined"
                  sx={{ minWidth: 80 }}
                />
              </Badge>
              <TextField
                size="small"
                placeholder="Search company, reg no, notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Alert severity="info" icon={<RefreshIcon />}>
              Loading wind up tracker data...
            </Alert>
          ) : filteredGroups.length === 0 ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              {search ? 'No companies match your search criteria.' : 'No companies are currently flagged for wind up.'}
            </Alert>
          ) : (
            <Stack spacing={2}>
              {filteredGroups.map((group) => (
                <Accordion key={group.jurisdiction} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <JurisdictionBadge
                        jurisdiction={group.jurisdiction}
                        variant="outlined"
                        showCount
                        count={group.companies.length}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {group.companies.filter((c) => {
                          const status = String(c.status || '').toUpperCase();
                          return status === 'WOUND_UP' || status === 'DISSOLVED';
                        }).length} wound up, {group.companies.filter((c) => {
                          const status = String(c.status || '').toUpperCase();
                          return !(status === 'WOUND_UP' || status === 'DISSOLVED');
                        }).length} pending
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <Box component="thead">
                        <Box
                          component="tr"
                          sx={{
                            '& th': {
                              textAlign: 'left',
                              p: 1.5,
                              borderBottom: '2px solid',
                              borderColor: 'divider',
                              fontWeight: 'bold',
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <Box component="th" width="25%">Company</Box>
                          <Box component="th" width="12%">Reg No</Box>
                          <Box component="th" width="18%">Status</Box>
                          <Box component="th" width="35%">Wind Up Notes</Box>
                          <Box component="th" width="10%">Actions</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {group.companies.map((c) => {
                          const status = String(c.status || '').toUpperCase();
                          const isWoundUp = status === 'WOUND_UP' || status === 'DISSOLVED';
                          return (
                            <Box
                              component="tr"
                              key={c.id}
                              sx={{
                                '& td': {
                                  p: 1.5,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  verticalAlign: 'top',
                                },
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              <Box component="td">
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      fontSize: 14,
                                      bgcolor: isWoundUp ? 'error.main' : 'warning.main',
                                    }}
                                  >
                                    {c.name?.charAt(0) || '?'}
                                  </Avatar>
                                  <Link
                                    component={RouterLink}
                                    to={`/companies/${encodeURIComponent(c.id)}`}
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {c.name}
                                  </Link>
                                </Stack>
                              </Box>
                              <Box component="td">
                                <Typography variant="body2" color="text.secondary">
                                  {c.registrationNumber || '—'}
                                </Typography>
                              </Box>
                              <Box component="td">
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    icon={isWoundUp ? <CancelIcon /> : <WarningAmberIcon />}
                                    color={isWoundUp ? 'error' : 'warning'}
                                    label={c.status || 'PENDING'}
                                  />
                                  {isWoundUp ? (
                                    <Tooltip title="Mark this company as active again">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => updateStatus(c.id, 'ACTIVE')}
                                      >
                                        Reactivate
                                      </Button>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title="Confirm wind up status">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={() => updateStatus(c.id, 'WOUND_UP')}
                                      >
                                        Wind Up
                                      </Button>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Box>
                              <Box component="td">
                                <CommentsLog
                                  title=""
                                  entries={((c.comments ? [{ id: '0', text: c.comments, createdAt: new Date(), author: 'Import', context: 'Wind Up' }] : []) as any)}
                                  onAdd={async (text: string) => {
                                    try {
                                      const newComments = c.comments ? `${c.comments}\n${text}` : text;
                                      await companiesAPI.update(c.id, { comments: newComments });
                                      toast.success('Note added');
                                      await fetchGroups();
                                    } catch (e: any) {
                                      toast.error('Failed to add note');
                                      // eslint-disable-next-line no-console
                                      console.error(e);
                                    }
                                  }}
                                  placeholder="Add wind up note..."
                                />
                              </Box>
                              <Box component="td">
                                <Tooltip title="Download PDF Report">
                                  <IconButton
                                    aria-label="Download PDF report"
                                    size="small"
                                    color="secondary"
                                    onClick={async () => {
                                      try {
                                        const response = await companiesAPI.downloadReport(String(c.id));
                                        const blob = new Blob([response.data], { type: 'application/pdf' });
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = `${c.name.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                        toast.success('Report downloaded successfully');
                                      } catch (e: any) {
                                        toast.error('Failed to download report');
                                        console.error(e);
                                      }
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default WindUpTracker;

